import { memo, useContext } from 'react';

import styled from '@emotion/styled';
import { themeFont } from '../lib/themeFont';
import useDateDisplayWithFormat from '../lib/useDateDisplayWithFormat';
import { WWContext, type WWContextValue } from './WWContext';

import { isWwsWeddingWebsiteV2 } from '../types/graphql';

import type { ThemeProps } from '@emotion/react';
import type { CSSObject } from '@emotion/styled';
import { buildEditBasicInfoContextMenuButtons } from './InlineEdit/InlineEditMessageFactory';
import InlineEditHandler from './InlineEdit/InlineEditHandler/InlineEditHandler';

// override font-size on mobile to accommodate large city names
const StylishWrap = styled('div')<{ hideOnHorizontal: boolean }>`
  letter-spacing: 2.23px;
  line-height: 1.3;
  display: none;
  flex-direction: column;
  justify-content: center;
  margin: 12px auto;
  padding: 0 16px;
  width: fit-content;

  @media (min-width: 768px) {
    display: ${(p) => (p.hideOnHorizontal ? 'none' : 'flex')};
    justify-content: center;
    padding: 0 16px;
    flex-direction: row;
    margin: 52px auto 20px;

    .verticalLayout.homePage & {
      display: flex;
      justify-content: flex-start;
      padding: 0;
      flex-direction: column;
      margin: 0 auto 20px 0;
    }
  }

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.components.DateLocationLockup}

  ${themeFont('s2')}

  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;

const DateDiv = styled('div')`
  padding: 0 36px 20px;

  border-bottom: 1px solid
    ${({ theme }: ThemeProps) =>
      theme.components.DateLocationLockup?.borderColor || theme.fonts.s4.color};

  @media (min-width: 640px) {
    border-bottom: none;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 20px 72px 20px 0;
    width: 315px;

    border-right: 1px solid
      ${({ theme }: ThemeProps) => {
        return (
          theme.components.DateLocationLockup?.borderColor ||
          theme.fonts.s4.color
        );
      }};

    .verticalLayout.homePage & {
      justify-content: flex-start;
      padding: 0;
      width: auto;
      border-right: none;
    }
  }
`;

const Location = styled('div')`
  padding: 20px 36px 0;
  @media (min-width: 768px) {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 20px 0 20px 72px;
    width: 315px;
  }

  .verticalLayout.homePage & {
    padding: 0;
    width: auto;

    br {
      content: ', ';
    }
    br:after {
      content: ', ';
    }
  }
`;

const Detail = styled('div')`
  text-align: center;
  .verticalLayout.homePage & {
    text-align: left;

    @media (min-width: 768px) and (max-width: 900px) {
      font-size: 0.75em;
    }
  }
`;

function DateLocationLockup() {
  const { data } = useContext<WWContextValue>(WWContext);

  const formattedDate = useDateDisplayWithFormat('natural');

  if (isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    const hideOnHorizontal =
      !data?.weddingWebsiteResult.weddingLocation || !formattedDate;

    const [city, state] = data?.weddingWebsiteResult?.weddingLocation?.split(
      ','
    ) || ['', ''];

    return (
      <InlineEditHandler
        contextMenuButtons={buildEditBasicInfoContextMenuButtons()}
      >
        <StylishWrap hideOnHorizontal={hideOnHorizontal}>
          {formattedDate && (
            <DateDiv>
              <Detail>
                <span>
                  {data?.weddingWebsiteResult.hideDate
                    ? 'Date TBA'
                    : formattedDate}
                </span>
              </Detail>
            </DateDiv>
          )}
          {data?.weddingWebsiteResult.weddingLocation && (
            <Location>
              <Detail>
                {city}
                <br />
                {state}
              </Detail>
            </Location>
          )}
        </StylishWrap>
      </InlineEditHandler>
    );
  }

  return null;
}

export default memo(DateLocationLockup);
